/**
 * USE RESOLVED BUSINESS — "questa azienda è già a sistema?", chiesto al backend.
 *
 * Esiste perché la stessa domanda viene posta da più schermate (la smart inbox sui dati
 * estratti di un messaggio, il pannello "Crea preventivo") e prima ognuna se la rispondeva
 * per conto suo: il backend cercava per dominio all'arrivo dell'email, la UI per nome dal
 * vivo. Le due risposte si contraddicevano davanti all'utente — la stessa azienda risultava
 * "non a sistema" in una schermata e correttamente collegata in quella accanto (TS-82).
 *
 * La regola non sta qui: sta in `BusinessLocalService.resolve` ed è raggiunta via
 * `GET /businesses/{groupId}/resolve`. Questo hook è solo il modo di chiamarla da React.
 *
 * È volutamente **live** e non legge il flag `businessExists` del messaggio: quel flag è uno
 * snapshot preso all'ingestione e non viene più ricalcolato, quindi un'azienda censita dopo
 * l'arrivo dell'email vi risulterebbe assente per sempre.
 */

import { useEffect, useState } from 'react';
import { useCurrentGroupId } from '../../auth/authSelectors';
import { businessApi } from './businessApi';
import type { Business } from './businessMappers';

/**
 * `idle`: niente da cercare (nessun dato di contatto, o gruppo non ancora noto).
 * `exists` / `missing`: risposta del backend. In caso di errore si torna a `idle`:
 * best-effort, meglio non dire nulla che affermare il falso in una direzione o nell'altra.
 */
export type ResolvedBusinessStatus = 'idle' | 'loading' | 'exists' | 'missing';

export interface ResolvedBusiness {
  status: ResolvedBusinessStatus;
  business: Business | null;
}

export function useResolvedBusiness(
  name?: string | null,
  domain?: string | null,
  emailAddress?: string | null,
): ResolvedBusiness {
  const groupId = useCurrentGroupId();
  const [resolved, setResolved] = useState<ResolvedBusiness>({ status: 'idle', business: null });

  // Le dipendenze sono i tre valori primitivi, non un oggetto: un literal `{name, domain}`
  // cambierebbe identità a ogni render del parent e rifarebbe la fetch all'infinito.
  const trimmedName = (name ?? '').trim();
  const trimmedDomain = (domain ?? '').trim();
  const trimmedEmail = (emailAddress ?? '').trim();

  useEffect(() => {
    if (!groupId || (!trimmedName && !trimmedDomain && !trimmedEmail)) {
      setResolved({ status: 'idle', business: null });
      return;
    }
    let cancelled = false;
    // Reset esplicito a ogni cambio di input: senza, per un render resterebbe visibile la
    // risposta relativa all'azienda precedente.
    setResolved({ status: 'loading', business: null });
    businessApi
      .resolve(groupId, {
        name: trimmedName || undefined,
        domain: trimmedDomain || undefined,
        emailAddress: trimmedEmail || undefined,
      })
      .then((business) => {
        if (cancelled) return;
        setResolved(
          business
            ? { status: 'exists', business }
            : { status: 'missing', business: null },
        );
      })
      .catch(() => {
        if (!cancelled) setResolved({ status: 'idle', business: null });
      });
    return () => { cancelled = true; };
  }, [groupId, trimmedName, trimmedDomain, trimmedEmail]);

  return resolved;
}
